import React, { useState, useRef, useEffect } from "react";
import { Box, Link } from "@mui/material";
import BasicButton from "components/v2/common/BasicButton";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { dispatcherActions, messageActions, userActions } from "_actions";
import { useDispatch } from "react-redux";
import _ from "lodash";
import { cleanResponseText, getCurrentUser, isObjectEmpty } from "_helpers";
import {
  dispatcherConstants,
  platformConstants,
  requestOrigin,
} from "_constants";
import { CrossDispatchWarningModal } from "components/v2/views/Messages/CrossDispatchWarningModal";
import { validate as validateUUID } from "uuid";
import { QuillEditor } from "components/common";

export default function ReplyBox({
  companyReplied,
  markAsDone,
  currentUser,
  conversation,
  link,
  openedMessageId,
  replyText,
  onReplyTextChange,
  isEmail = false,
}) {
  const dispatch = useDispatch();
  const editorRef = useRef(null);
  const lastEmittedText = useRef(replyText || "");

  const [isDispatching, setIsDispatching] = useState(false);
  const [crossDispatchWarningModal, setCrossDispatchWarningModal] =
    useState(false);
  const [crossDispatchWarningDismissed, setCrossDispatchWarningDismissed] =
    useState(false);

  const userId = _.get(currentUser, "id") || _.get(getCurrentUser(), "id");
  const messageId = openedMessageId || _.get(conversation, "id");
  const isResolved =
    !isObjectEmpty(_.get(markAsDone, "id")) ||
    !isObjectEmpty(_.get(companyReplied, "id")) ||
    _.get(conversation, "isDispatchedSuccessfully");

  useEffect(() => {
    if ((replyText || "") === lastEmittedText.current) {
      return;
    }
    lastEmittedText.current = replyText || "";
    const html = isEmail
      ? replyText || ""
      : (replyText || "").replace(/\n/g, "<br>");
    editorRef.current?.setEditor(html);
  }, [replyText, isEmail]);

  const handleEditorChange = (text) => {
    lastEmittedText.current = text || "";
    onReplyTextChange(text);
  };

  const handleMarkAsDone = () => {
    if (!messageId || !userId) {
      return;
    }
    dispatch(userActions.markInboxMsgAsDone(messageId, userId));
  };

  const refreshConversation = (messageId) => {
    if (!messageId) return;
    if (isEmail) {
      const platform = validateUUID(messageId)
        ? platformConstants.TEAMS
        : platformConstants.TRENDS;
      dispatch(messageActions.getFullEmailConversation(messageId, platform));
    } else {
      if (validateUUID(messageId)) {
        dispatch(
          messageActions.getConversation(
            messageId,
            {},
            requestOrigin.INBOX_DETAILS_V2,
          ),
        );
      } else {
        dispatch(
          messageActions.getConversationFromTrends(
            messageId,
            {},
            null,
            requestOrigin.INBOX_DETAILS_V2,
            true,
          ),
        );
      }
    }
  };

  const handleSubmit = () => {
    if (!messageId || !replyText) {
      return;
    }

    const sourceName = _.get(conversation, "sourceName");
    const payload = {
      openedMessageId: messageId,
      isDispatched: false,
      reply: cleanResponseText(replyText, sourceName),
      userId,
      source: sourceName,
      isV2Enabled: _.get(conversation, "isV2Enabled"),
      draftId: conversation?.draft?.id,
      crossDispatchWarningDismissed,
    };

    setIsDispatching(true);

    dispatch(dispatcherActions.dispatchMessage(messageId, payload))
      .then((result) => {
        if (result.success) {
          refreshConversation(messageId);
          onReplyTextChange("");
          editorRef.current.clearEditor();
          setCrossDispatchWarningDismissed(false);
        } else if (
          !result.success &&
          result?.response?.errorCode ===
            dispatcherConstants.POSSIBLE_CROSS_DISPATCH_ERROR_CODE
        ) {
          setCrossDispatchWarningModal(true);
        }
      })
      .catch((error) => {
        console.error("An error occurred when dispatching message: " + error);
      })
      .finally(() => {
        setIsDispatching(false);
      });
  };

  return (
    <Box sx={styles.wrapper}>
      <Box sx={styles.editorStyle}>
        <QuillEditor
          placeholder="Write a reply..."
          defaultText={""}
          handleTextChange={handleEditorChange}
          isEmail={isEmail}
          ref={editorRef}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 12px",
        }}
      >
        <div>
          {link && (
            <Link
              target="_blank"
              rel="noopener noreferrer"
              href={`${link}`}
              sx={styles.textStyle}
              underline="none"
            >
              View on source
            </Link>
          )}
        </div>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          {isResolved ? (
            <Box sx={styles.resolvedText}>
              Resolved
              <CheckCircleIcon fontSize="small" />
            </Box>
          ) : (
            <Box>
              <BasicButton
                style={styles.outlineBtn}
                label={"Mark as resolved"}
                action={handleMarkAsDone}
              />
            </Box>
          )}

          <BasicButton
            style={styles.greenBtn}
            label={"Reply"}
            action={handleSubmit}
            disabled={isDispatching}
          />
        </Box>
      </Box>
      <CrossDispatchWarningModal
        open={crossDispatchWarningModal}
        handleClose={() => {
          setCrossDispatchWarningModal(false);
          setCrossDispatchWarningDismissed(true);
        }}
      />
    </Box>
  );
}

const styles = {
  wrapper: {
    border: "1px solid rgb(227, 221, 211)",
    borderRadius: "12px",
  },
  greenBtn: {
    fontSize: "13px",
    px: "16px",
    height: "38px",
    borderRadius: "8px",
    width: "max-content",
  },
  outlineBtn: {
    fontSize: "13px",
    px: "20px",
    height: "38px",
    borderRadius: "8px",
    border: "1px solid #d9d3c9",
    backgroundColor: "#ffffff",
    width: "max-content",
    color: "#003300",
    ":hover": {
      backgroundColor: "#ffffff",
      color: "#002900",
    },
  },
  resolvedText: {
    display: "flex",
    alignItems: "center",
    fontFamily: "FustatBold",
    fontSize: "14px",
    color: "#003300",
    gap: "8px",
  },
  textStyle: {
    fontFamily: "FustatSemiBold",
    fontSize: "14px",
    padding: "0",
    color: "#003300",
    textTransform: "none",
    letterSpacing: "-0.14px",
    ":hover": {
      color: "#285a55",
      backgroundColor: "transparent",
      textDecoration: "none",
    },
    ":focus": {
      color: "#003300",
      textDecoration: "none",
    },
  },
  editorStyle: {
    fontFamily: "FustatMedium",
    fontSize: "16px",
    color: "#0C2728",
    overflowWrap: "break-word",
    wordWrap: "break-word",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    maxHeight: "350px",
    overflowY: "auto",
    "& .text-editor": {
      border: "none",
    },
  },
};
